import { Col, Row, Select } from 'antd';
import React, { FC, MouseEventHandler, useState, KeyboardEvent } from 'react';
import { SelectProps } from 'shared/components/COSelect';
import {
  useFormContext,
  Controller,
  ControllerRenderProps,
  FieldValues,
} from 'react-hook-form';
import styles from './rhfSelect.module.scss';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import FieldLabel from 'shared/components/FieldLabel';
import clsx from 'clsx';
import Error from '../../Error';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import { debounce } from 'lodash';
import { ELEMENT_INPUT, KEYDOWN_ENTER } from 'constants/constants';

const RHFSelect: FC<SelectProps> = props => {
  const {
    name,
    disabled,
    isShowLabel,
    infoClick,
    info,
    label,
    listHeight,
    onChange,
    showSearch,
    onKeyDown,
    onKeyUp,
    placeholderstring,
    onFocus,
    loading,
    open,
    onDropdownVisibleChange,
    ...remainingProps
  } = props;

  const [showLabel, setshowLabel] = useState<boolean>(
    !!disabled ?? !!isShowLabel ?? false
  );
  const handleShowLabel = () => setshowLabel(true);
  const { control, setValue } = useFormContext();

  const handleInfoClick: MouseEventHandler<HTMLSpanElement> = e => {
    e.stopPropagation();
    infoClick?.();
  };

  const getPopupContainer = (trigger: HTMLElement): HTMLElement => {
    return (trigger.parentNode as HTMLElement) || trigger;
  };

  const setVal = debounce((event, inputElement) => {
    if (!onKeyUp) return;
    const enteredValue = inputElement?.value?.trim();
    setValue(name, enteredValue ?? null);
    onKeyUp(event);
  }, 150);

  const defaultOnKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!onKeyUp || !showSearch) return;
    const inputElement = event.currentTarget.querySelector(
      ELEMENT_INPUT
    ) as HTMLInputElement;
    if (event.key == KEYDOWN_ENTER) onKeyUp(event);
    else setVal(event, inputElement);
  };

  const defaultOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!onKeyDown || !showSearch) return;
    const inputElement = event.currentTarget.querySelector(
      ELEMENT_INPUT
    ) as HTMLInputElement;
    if (event.key == KEYDOWN_ENTER) {
      const enteredValue = inputElement?.value?.trim();
      setValue(name, enteredValue ?? null);
    }
    onKeyDown(event);
  };

  const handleFocus = (e: React.FocusEvent<HTMLElement, Element>) => {
    handleShowLabel();
    onFocus && onFocus(e);
  };

  const suffixIcon = (
    <Row align="middle">
      {info && (
        <Col onClick={handleInfoClick}>
          <InfoCircleOutlined className={styles.infoIcon} />
        </Col>
      )}
      <Col>
        {!disabled && loading ? <LoadingOutlined /> : <Arrow fontSize={44} />}
      </Col>
    </Row>
  );

  const handleOnChange =
    (field: ControllerRenderProps<FieldValues, string>) =>
    (value: string | number) => {
      field.onChange(value);
      onChange?.(value);
    };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { isTouched, error } }) => (
        <Row
          className={clsx({
            [styles.selectFieldContainer]: true,
            [styles.error]: !disabled && !!error?.message,
            [styles.loading]: !disabled && !!loading,
          })}>
          {label && (
            <Col span={24}>
              <FieldLabel
                showLabel={
                  disabled ||
                  showLabel ||
                  !!field.value ||
                  isTouched ||
                  !!error?.message
                }
                label={label}
                name={name}
                error={!disabled && !!error?.message}
              />
            </Col>
          )}
          <Col span={24}>
            <Select
              getPopupContainer={getPopupContainer}
              {...field}
              {...props}
              id={name}
              listHeight={listHeight ?? 156}
              data-testid={name}
              disabled={disabled}
              showSearch={showSearch}
              suffixIcon={suffixIcon}
              placeholder={placeholderstring}
              onFocus={handleFocus}
              onChange={handleOnChange(field)}
              onKeyUp={defaultOnKeyUp}
              onKeyDown={defaultOnKeyDown}
              open={open}
              onDropdownVisibleChange={onDropdownVisibleChange}
              {...remainingProps}
            />
          </Col>

          {!disabled && !!error?.message && <Error message={error?.message} />}
        </Row>
      )}
    />
  );
};

export default RHFSelect;
